import React, { useState } from 'react';
import axios from 'axios';
import { Package, Search, Plus, Edit, AlertTriangle, Archive, Sparkles, X, Copy, Check } from 'lucide-react';
import { Badge, Th, Td } from '../WMSShared';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms';

export default function ProductsTab({
  products, filteredProducts, search, setSearch,
  setEditItem, setForm, setModal, setLossModal, setLossForm, delProduct,
  cur = 'S$'
}) {
  const [descModal, setDescModal]     = useState(null); // { product_id, product_name, description }
  const [descLoading, setDescLoading] = useState(null); // product_id being generated
  const [copied, setCopied]           = useState(false);

  const generateDescription = async (product) => {
    setDescLoading(product.product_id);
    try {
      const res = await axios.post(`${API}/ai/product-description`, { product_id: product.product_id });
      setDescModal({ product_id: res.data.product_id, product_name: res.data.product_name, description: res.data.description });
    } catch (e) {
      alert(e.response?.data?.detail || 'Description generation failed. Ensure Ollama is running.');
    }
    setDescLoading(null);
  };

  const copyDescription = () => {
    navigator.clipboard.writeText(descModal.description);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [dupes, setDupes] = useState(null);
  const [merging, setMerging] = useState(false);

  const findDuplicates = async () => {
    try {
      const r = await axios.get(`${API}/products/duplicates`);
      setDupes(r.data);
    } catch {}
  };

  const mergeProduct = async (primaryId, secondaryId) => {
    setMerging(true);
    try {
      await axios.post(`${API}/products/merge`, { primary_id: primaryId, secondary_id: secondaryId });
      await findDuplicates();
      window.location.reload();
    } catch (e) {
      console.error('Merge failed:', e);
    }
    setMerging(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">
          Products <span className="text-gray-400 font-normal text-base">({products.length})</span>
        </h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text" placeholder="Search products..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <button onClick={findDuplicates}
            className="flex items-center gap-2 text-sm border border-amber-300 text-amber-700 px-3 py-2 rounded-lg hover:bg-amber-50 font-medium"
            data-testid="find-duplicates-btn">
            <AlertTriangle className="w-4 h-4" />Fix Duplicates
          </button>
          <button
            onClick={() => { setEditItem(null); setForm({ stock: 0, price: 0, cost: 0, min_stock: 5, weight: 0 }); setModal('product'); }}
            className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 shadow-sm"
          >
            <Plus className="w-4 h-4" />Add Product
          </button>
        </div>
      </div>

      {/* Duplicates Panel */}
      {dupes !== null && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="duplicates-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-amber-800">
              {dupes.length > 0 ? `${dupes.length} Duplicate Product Group(s) Found` : 'No duplicates found'}
            </p>
            <button onClick={() => setDupes(null)} className="text-amber-500 hover:text-amber-800"><X className="w-4 h-4" /></button>
          </div>
          {dupes.map((group, i) => (
            <div key={i} className="bg-white rounded-lg border p-3 mb-2">
              <p className="text-sm font-semibold mb-2">{group.name} <span className="text-gray-400">({group.count} records)</span></p>
              <div className="space-y-1.5">
                {group.products.map(p => (
                  <div key={p.product_id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                    <div>
                      <span className="font-mono">{p.product_id}</span>
                      <span className="ml-2 text-gray-500">SKU: {p.sku}</span>
                      <span className="ml-2">Stock: <strong>{p.stock}</strong></span>
                      {p.has_shopify && <span className="ml-2 text-blue-600 font-semibold">SHOPIFY</span>}
                      {p.source === 'shopify' && <span className="ml-1 text-blue-500">(synced)</span>}
                    </div>
                    <div className="flex gap-2">
                      {group.products.filter(x => x.product_id !== p.product_id).map(other => (
                        <button key={other.product_id} onClick={() => mergeProduct(p.product_id, other.product_id)}
                          disabled={merging}
                          className="text-[11px] bg-emerald-500 text-white px-3 py-1 rounded-lg hover:bg-emerald-600 disabled:opacity-50 font-medium">
                          {merging ? 'Merging...' : `Keep this, merge ${other.sku}`}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>SKU</Th><Th>Product</Th><Th>Brand</Th><Th>Price</Th><Th>Cost</Th>
                <Th>Stock</Th><Th>Sold</Th><Th>Warehouse</Th><Th>Status</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredProducts.map(p => (
                <tr key={p.product_id} className="hover:bg-gray-50">
                  <Td className="font-mono text-xs text-gray-500">{p.sku}</Td>
                  <Td className="font-medium max-w-[200px] truncate">{p.name}</Td>
                  <Td className="text-gray-500">{p.brand || p.category}</Td>
                  <Td className="font-medium">{cur}{p.price}</Td>
                  <Td className="text-gray-500">
                    {p.cost > 0 ? `${cur}${p.cost}` : <span className="text-xs text-gray-300 italic">pending</span>}
                  </Td>
                  <Td>
                    <span className={p.stock === 0 ? 'text-red-600 font-bold' : p.stock <= (p.min_stock || 5) ? 'text-amber-600 font-semibold' : 'text-gray-900'}>
                      {p.stock}
                    </span>
                  </Td>
                  <Td className="text-gray-500">{p.sold}</Td>
                  <Td className="text-xs text-gray-500">{p.warehouse_id}</Td>
                  <Td><Badge status={p.status} /></Td>
                  <Td>
                    <div className="flex gap-1">
                      <button
                        onClick={() => { setEditItem(p); setForm(p); setModal('product'); }}
                        className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-500" title="Edit"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => generateDescription(p)}
                        disabled={descLoading === p.product_id}
                        className="p-1.5 rounded hover:bg-purple-50 text-gray-400 hover:text-purple-500 disabled:opacity-50" title="AI Generate Description"
                      >
                        <Sparkles className={`w-3.5 h-3.5 ${descLoading === p.product_id ? 'animate-pulse text-purple-400' : ''}`} />
                      </button>
                      <button
                        onClick={() => { setLossModal(p); setLossForm({ quantity: 1, category: 'theft', reason: '', evidence: '' }); }}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500" title="Report Loss"
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => delProduct(p.product_id)}
                        className="p-1.5 rounded hover:bg-amber-50 text-gray-400 hover:text-amber-500" title="Archive"
                      >
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
          <span>{filteredProducts.length} of {products.length} products</span>
          <span className="flex items-center gap-1 text-purple-500">
            <Sparkles className="w-3 h-3" /> Click the sparkle icon to generate AI product descriptions
          </span>
        </div>
      </div>

      {/* AI Description Modal */}
      {descModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />AI-Generated Description
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">{descModal.product_name}</p>
              </div>
              <button onClick={() => { setDescModal(null); setCopied(false); }} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 border text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                {descModal.description}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                onClick={() => { setDescModal(null); setCopied(false); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={copyDescription}
                className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg font-medium transition-all ${copied ? 'bg-emerald-500 text-white' : 'bg-purple-500 text-white hover:bg-purple-600'}`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
